'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Heart, RotateCcw, ThumbsDown, X } from 'lucide-react'
import { env } from '~/env'
import {
  flushInteractions,
  trackCardInteraction,
  type InteractionAction,
} from '~/lib/activity-tracking'
import SwipeCard, { type SwipeEvent } from './SwipeCard'

export interface DeckEntry {
  event: SwipeEvent
  score: number
  score_breakdown: Record<string, number>
  position_in_deck: number
  // Unified scorer: short reason + source subsection shown instead of a %.
  why?: string
  source?: string
  bucket?: 'STRONG' | 'GOOD' | 'FAIR' | null
}

/**
 * The matching game: one swipe session against a scored deck.
 * Swipe right (or ❤) = like (also saves the event), swipe left (or ✗) = skip,
 * the smaller 👎 = dislike ("show me less like this").
 */
export default function SwipeDeck({
  sessionId,
  deck,
  accessToken,
  onRestart,
}: {
  sessionId: string
  deck: DeckEntry[]
  accessToken: string
  onRestart: () => void
}) {
  const [index, setIndex] = useState(0)
  const [likedEvents, setLikedEvents] = useState<SwipeEvent[]>([])
  const [forcedDirection, setForcedDirection] = useState<'left' | 'right' | null>(null)
  // The action behind a forced fling ('left' animation is shared by skip and dislike)
  const pendingAction = useRef<InteractionAction | null>(null)
  const cardShownAt = useRef<number>(Date.now())
  const endedRef = useRef(false)

  const current = deck[index]
  const finished = index >= deck.length

  const endSession = useCallback(() => {
    if (endedRef.current) return
    endedRef.current = true
    flushInteractions()
    fetch(`${env.NEXT_PUBLIC_API_URL}/engagement/sessions/${sessionId}/end`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${accessToken}` },
      keepalive: true,
    }).catch(() => {})
  }, [sessionId, accessToken])

  // Flush pending interactions if the tab hides mid-game; end on unmount.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushInteractions()
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      flushInteractions()
    }
  }, [])

  useEffect(() => {
    if (finished) endSession()
  }, [finished, endSession])

  const advance = useCallback(
    (action: InteractionAction) => {
      const entry = deck[index]
      if (!entry) return
      trackCardInteraction(
        {
          event_id: entry.event.id,
          session_id: sessionId,
          section: 'SWIPE_DECK',
          action,
          dwell_ms: Date.now() - cardShownAt.current,
          position_in_deck: entry.position_in_deck,
        },
        accessToken,
      )
      if (action === 'LIKED') setLikedEvents((prev) => [...prev, entry.event])
      cardShownAt.current = Date.now()
      pendingAction.current = null
      setForcedDirection(null)
      setIndex((i) => i + 1)
    },
    [deck, index, sessionId, accessToken],
  )

  const handleSwipe = (direction: 'left' | 'right') => {
    advance(pendingAction.current ?? (direction === 'right' ? 'LIKED' : 'SKIPPED'))
  }

  const buttonAction = (action: InteractionAction) => {
    if (finished || forcedDirection) return
    pendingAction.current = action
    setForcedDirection(action === 'LIKED' ? 'right' : 'left')
  }

  if (finished) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-5 rounded-[22px] border border-[#EFE7D7] bg-[#FFFDF8] p-8 text-center">
        <span className="text-4xl">✨</span>
        <h2 className="text-xl font-bold text-[#2C2D2B]">
          {deck.length === 0 ? 'No new events to show right now' : 'That’s your deck!'}
        </h2>
        {deck.length > 0 && (
          <p className="text-sm text-[#5E594F]">
            You liked <b className="text-[#2C2D2B]">{likedEvents.length}</b> of{' '}
            {deck.length} events{likedEvents.length > 0 && ' — they’ve been added to your saved list'}.
          </p>
        )}
        {likedEvents.length > 0 && (
          <ul className="w-full space-y-1 text-left">
            {likedEvents.slice(0, 5).map((e) => (
              <li key={e.id}>
                <Link
                  href={`/events/${e.id}`}
                  className="block truncate rounded-lg px-3 py-2 text-sm font-semibold text-[#4E7A6F] hover:bg-[#75A096]/10"
                >
                  ❤ {e.title}
                </Link>
              </li>
            ))}
          </ul>
        )}
        <div className="flex flex-wrap justify-center gap-3">
          {likedEvents.length > 0 && (
            <Link
              href="/discovery?saved=1"
              className="rounded-full bg-[#DE866D] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#c9755e]"
            >
              View saved events
            </Link>
          )}
          <button
            type="button"
            onClick={onRestart}
            className="inline-flex items-center gap-2 rounded-full border border-[#D9CDB9] bg-[#FFFDF8] px-5 py-2.5 text-sm font-bold text-[#5E594F] hover:border-[#8A8475]"
          >
            <RotateCcw className="h-4 w-4" /> Play again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-6">
      <p className="text-sm text-[#8A8475]">
        {index + 1} of {deck.length}
      </p>
      <div className="relative h-[520px] w-full">
        {/* Render the top two cards so the next one peeks from behind */}
        {deck
          .slice(index, index + 2)
          .reverse()
          .map((entry) => {
            const isTop = entry === current
            return (
              <div
                key={entry.event.id}
                className="absolute inset-0"
                style={
                  isTop
                    ? undefined
                    : { transform: 'scale(0.95) translateY(14px)', filter: 'brightness(0.97)' }
                }
              >
                <SwipeCard
                  event={entry.event}
                  score={entry.score}
                  why={entry.why}
                  source={entry.source}
                  active={isTop}
                  onSwipe={handleSwipe}
                  forcedDirection={isTop ? forcedDirection : null}
                />
              </div>
            )
          })}
      </div>
      <div className="flex items-center gap-5">
        <button
          type="button"
          aria-label="Skip"
          onClick={() => buttonAction('SKIPPED')}
          className="flex h-14 w-14 items-center justify-center rounded-full border border-[#D9CDB9] bg-[#FFFDF8] text-[#8A8475] shadow-sm transition-transform hover:scale-105"
        >
          <X className="h-6 w-6" />
        </button>
        <button
          type="button"
          aria-label="Like"
          onClick={() => buttonAction('LIKED')}
          className="flex h-16 w-16 items-center justify-center rounded-full bg-[#DE866D] text-white shadow-md transition-transform hover:scale-105"
        >
          <Heart className="h-7 w-7" />
        </button>
        <button
          type="button"
          aria-label="Not interested — show me less like this"
          title="Show me less like this"
          onClick={() => buttonAction('DISLIKED')}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D9CDB9] bg-[#FFFDF8] text-[#8A8475] transition-transform hover:scale-105"
        >
          <ThumbsDown className="h-4 w-4" />
        </button>
      </div>
      <p className="text-xs text-[#8A8475]">
        Swipe right to like & save · swipe left to skip · 👎 to see less like this
      </p>
    </div>
  )
}
